import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Pencil, RefreshCw } from 'lucide-react';
import '../../../services/ConsoleHelper.js';
import '../../../tailwindcss/Tailwind.css';
import { buildApiUrl } from '../../../services/apiConfig.js';

// Helper function to parse markdown-style bold text
const parseFormattedText = (text) => {
  if (!text) return null;
  
  // Split by **text** pattern
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  
  return parts.map((part, index) => {
    // Check if this part is bold (wrapped in **)
    if (part.startsWith('**') && part.endsWith('**')) {
      // Remove ** and make it bold with primary color
      const boldText = part.slice(2, -2);
      return <strong key={index} className="font-bold text-primary">{boldText}</strong>;
    }
    return <span key={index}>{part}</span>;
  });
};

function DashboardSummaryWidget({
  id,
  title = "Dashboard Summary",
  prompt: initialPrompt = "",
  dashboardId,
  dashboardName,
  onContentChange,
  widgetContents,
  userId,
  useCaseId,
  widgets,
  ...props
}) {
  // Component render log
  console.log(`[DashboardSummaryWidget RENDER] id=${id}, dashboardId=${dashboardId}, dashboardName=${dashboardName}, widgetContents keys:`, Object.keys(widgetContents || {}));
  
  const [editing, setEditing] = useState(false);
  const [prompt, setPrompt] = useState(initialPrompt);
  const [tempPrompt, setTempPrompt] = useState(initialPrompt);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isHovering, setIsHovering] = useState(false);
  const [summary, setSummary] = useState({
    summary_points: [
      { title: "Loading Summary", content: "Analyzing dashboard widgets..." },
      { title: "Please Wait", content: "Gathering insights from your dashboard..." },
      { title: "Almost Ready", content: "Preparing your dashboard overview..." },
      { title: "Dashboard Analysis", content: "Evaluating widget data and relationships..." }
    ],
    overall_summary: "Analyzing your dashboard content..."
  });
  
  // Track the last fetched dashboard and content hash
  const [lastFetchedDashboardId, setLastFetchedDashboardId] = useState(null);
  const [lastContentHash, setLastContentHash] = useState("");
  
  // Refs to prevent multiple API calls
  const fetchTimeoutRef = useRef(null);
  const initialFetchDoneRef = useRef(false);
  const apiCallInProgressRef = useRef(false);
  const abortControllerRef = useRef(null);
  const API_HOST = process.env.REACT_APP_API_URL || "50.17.67.70";
  const RUN_UPDATE_ENDPOINT = buildApiUrl('AUTH', '/run-update-backend');
  const DASHBOARD_SUMMARY_ENDPOINT = buildApiUrl('CUSTOMER_SUMMARY', '/dashboard-summary');

  // Component mount/unmount log
  useEffect(() => {
    console.log(`[DashboardSummaryWidget MOUNTED] id=${id}, endpoint=${DASHBOARD_SUMMARY_ENDPOINT}`);
    return () => {
      console.log(`[DashboardSummaryWidget UNMOUNTED] id=${id}`);
    };
  }, []); // Empty deps = runs once on mount

  // Function to generate a simple hash of widget contents
  const generateContentHash = useCallback(() => {
    const contentKeys = Object.keys(widgetContents || {}).filter(k => k !== id).sort();
    if (contentKeys.length === 0) {
      console.log("generateContentHash: No widget content to hash");
      return "";
    }

    // Create a more robust hash using first/last 100 chars of content
    const hash = contentKeys.map(key => {
      const content = widgetContents[key];
      const contentStr = typeof content === 'string' ? content : JSON.stringify(content);
      const len = contentStr?.length || 0;
      const preview = contentStr?.substring(0, 100) || '';
      const ending = len > 100 ? contentStr?.substring(len - 50) : '';
      return `${key}:${len}:${preview}:${ending}`;
    }).join('|');
    
    console.log(`generateContentHash: ${contentKeys.length} widgets, hash length: ${hash.length}`);
    return hash;
  }, [widgetContents, id]);

  // Function to fetch summary
  const fetchSummary = useCallback(async (force = false) => {
    // Don't fetch if already loading or no dashboard
    if (!dashboardId || !dashboardName) {
      console.log("No dashboard information available");
      return;
    }

    // Generate a hash of the current widget contents
    const currentContentHash = generateContentHash();
    console.log(`fetchSummary: dashboard=${dashboardId}, force=${force}, hash length=${currentContentHash?.length || 0}`);

    // Don't fetch if we've already fetched for this dashboard with the same content
    if (!force && dashboardId === lastFetchedDashboardId && currentContentHash === lastContentHash) {
      console.log(`fetchSummary: Skipping - cached (dashboard=${dashboardId}, hash match)`);
      return;
    }

    // Check if we have any widget content
    const hasContent = Object.keys(widgetContents || {}).filter(key => key !== id).length > 0;
    if (!hasContent) {
      console.log("fetchSummary: No widget content available yet, waiting...");
      return;
    }
    console.log(`fetchSummary: Found ${Object.keys(widgetContents || {}).filter(key => key !== id).length} other widgets with content`);

    // Prevent multiple simultaneous API calls
    if (apiCallInProgressRef.current) {
      console.log("API call already in progress, canceling previous call");
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    }

    // Set loading state and mark API call as in progress
    setLoading(true);
    apiCallInProgressRef.current = true;
    console.log(`Starting API call for dashboard: ${dashboardName}`);

    // Create a new abort controller for this request
    abortControllerRef.current = new AbortController();

    try {
      // Prepare widgets for API call
      const dashboardWidgets = Object.entries(widgetContents || {})
        .map(([widgetId, content]) => {
          // Skip if this is our own widget
          if (widgetId === id) return null;
          
          // Get widget info
          const widget = widgets?.find(w => w.id === widgetId);
          const widgetTitle = widget?.title || `Widget ${widgetId}`;
          const widgetType = widget?.chartPrompt ? 'visualization' : 'text';
          
          // Process content
          let formattedContent;
          try {
            // Format content based on type
            if (typeof content === 'string') {
              if (content.trim().startsWith('{') || content.trim().startsWith('[')) {
                try {
                  const parsedJson = JSON.parse(content);
                  if (parsedJson.summary_points && parsedJson.overall_summary) {
                    formattedContent = `Dashboard Summary: ${parsedJson.overall_summary}`;
                  } else {
                    formattedContent = JSON.stringify(parsedJson, null, 2);
                  }
                } catch (e) {
                  formattedContent = content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
                }
              } else {
                formattedContent = content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
              }
            } else if (typeof content === 'object') {
              formattedContent = JSON.stringify(content, null, 2);
            } else {
              formattedContent = String(content || "");
            }
          } catch (e) {
            console.error(`Error processing content for widget ${widgetId}:`, e);
            formattedContent = `[Error processing widget content: ${e.message}]`;
          }
          
          // Skip empty content
          if (!formattedContent || formattedContent.trim().length === 0) {
            return null;
          }
          
          // Truncate large content
          const MAX_CONTENT_LENGTH = 5000;
          if (formattedContent.length > MAX_CONTENT_LENGTH) {
            console.log(`Truncating widget ${widgetTitle} content from ${formattedContent.length} to ${MAX_CONTENT_LENGTH} chars`);
            formattedContent = formattedContent.substring(0, MAX_CONTENT_LENGTH) + '... [content truncated]';
          }
          
          return {
            widget_id: widgetId,
            widget_type: widgetType,
            title: widgetTitle,
            content: formattedContent,
            metadata: null
          };
        })
        .filter(widget => widget !== null && widget.content && widget.content.length > 0);
      
      if (dashboardWidgets.length === 0) {
        console.log("No meaningful widget content to summarize");
        setLoading(false);
        apiCallInProgressRef.current = false;
        return;
      }
      
      console.log(`Sending ${dashboardWidgets.length} widgets to dashboard-summary API...`);
      console.log(`API endpoint: ${DASHBOARD_SUMMARY_ENDPOINT}`);
      
      // Add a unique timestamp to prevent caching
      const timestamp = Date.now();
      
      const requestBody = {
        dashboard_id: dashboardId,
        dashboard_name: dashboardName,
        widgets: dashboardWidgets,
        user_prompt: tempPrompt,
        user_id: userId,
        use_case_id: useCaseId,
        agent_id: id,
        domain_id: "00001"
      };
      
      console.log(`Request payload:`, JSON.stringify(requestBody, null, 2));
      
      // Make the API call
      const response = await fetch(`${DASHBOARD_SUMMARY_ENDPOINT}?t=${timestamp}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate'
        },
        body: JSON.stringify(requestBody),
        signal: abortControllerRef.current.signal
      });
      
      console.log(`Response status: ${response.status} ${response.statusText}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`API error response (${response.status}):`, errorText);
        throw new Error(`API responded with status ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      console.log("Received dashboard summary from API:", data);
      
      // Update the summary
      setSummary(data);
      
      // Update content in parent component if different
      if (typeof onContentChange === 'function' && id !== undefined) {
        const newContent = JSON.stringify(data);
        const currentContent = widgetContents[id];
        
        if (newContent !== currentContent) {
          onContentChange(id, newContent);
        }
      }
      
      // Update tracking variables
      setLastFetchedDashboardId(dashboardId);
      setLastContentHash(currentContentHash);
      initialFetchDoneRef.current = true;
      setError(null);
      console.log(`Successfully fetched and cached summary for dashboard ${dashboardId}`);
      
    } catch (err) {
      // Ignore aborted requests
      if (err.name === 'AbortError') {
        console.log("Request was aborted");
        return;
      }
      
      console.error("Error generating dashboard summary:", err);
      setError(`Failed to generate summary: ${err.message}. Please try refreshing.`);
      
      // Set fallback summary
      setSummary({
        summary_points: [
          { title: "Error", content: "Unable to generate summary. Please try again." },
          { title: "Possible Causes", content: "Too much widget data or API connection issue." },
          { title: "Suggestion", content: "Try refreshing the page or reducing the number of widgets." }
        ],
        overall_summary: "Error generating dashboard summary. Please try again."
      });
      
      // Still mark as attempted so we can retry on content change
      initialFetchDoneRef.current = true;
      console.log(`Error fetching summary, but marking as attempted`);
    } finally {
      // Reset loading state and API call flag
      setLoading(false);
      apiCallInProgressRef.current = false;
      abortControllerRef.current = null;
    }
  }, [API_HOST, dashboardId, dashboardName, generateContentHash, id, lastContentHash, lastFetchedDashboardId, tempPrompt, userId, useCaseId, widgets, widgetContents]);

  // Handle initial prompt setup
  useEffect(() => {
    if (initialPrompt !== prompt) {
      console.log("Updating prompt state with initialPrompt:", initialPrompt);
      setPrompt(initialPrompt);
      setTempPrompt(initialPrompt);
    }
  }, [initialPrompt, prompt]);

  // Single effect to handle dashboard changes and initial load
  useEffect(() => {
    console.log(`[DashboardSummary] Effect triggered - dashboardId=${dashboardId}, widgetContents keys:`, Object.keys(widgetContents || {}));
    
    // Clear any existing timeout
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = null;
    }
    
    // Cancel any in-progress API call
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    // Reset state when dashboard changes
    if (dashboardId !== lastFetchedDashboardId) {
      console.log(`Dashboard changed: ${lastFetchedDashboardId} -> ${dashboardId}, resetting state`);
      initialFetchDoneRef.current = false;
      apiCallInProgressRef.current = false;
      setLastContentHash("");
      
      setSummary({
        summary_points: [
          { title: "Loading Summary", content: "Analyzing dashboard widgets..." },
          { title: "Please Wait", content: "Gathering insights from your dashboard..." },
          { title: "Almost Ready", content: "Preparing your dashboard overview..." },
          { title: "Dashboard Analysis", content: "Evaluating widget data and relationships..." }
        ],
        overall_summary: `Loading summary for ${dashboardName || 'dashboard'}...`
      });
    }
    
    // Check if we should fetch
    const hasDashboard = !!dashboardId && !!dashboardName;
    const widgetKeys = Object.keys(widgetContents || {}).filter(key => key !== id);
    const hasContent = widgetKeys.length > 0;
    const needsInitialFetch = !initialFetchDoneRef.current && !apiCallInProgressRef.current;
    
    console.log(`[Trigger Check] hasDashboard=${hasDashboard} (id=${dashboardId}, name=${dashboardName})`);
    console.log(`[Trigger Check] hasContent=${hasContent}, widgetCount=${widgetKeys.length}, widgets=[${widgetKeys.join(', ')}]`);
    console.log(`[Trigger Check] initialFetchDone=${initialFetchDoneRef.current}, apiInProgress=${apiCallInProgressRef.current}`);
    console.log(`[Trigger Check] lastFetchedDashboard=${lastFetchedDashboardId}, lastContentHash length=${lastContentHash?.length || 0}`);
    
    // If content just appeared and we haven't fetched yet, reset the flag to allow fetch
    if (hasDashboard && hasContent && !needsInitialFetch && !lastContentHash) {
      console.log("[Reset] Content appeared after initial check - resetting initialFetchDone to allow fetch");
      initialFetchDoneRef.current = false;
    }
    
    const shouldFetch = hasDashboard && hasContent && !initialFetchDoneRef.current && !apiCallInProgressRef.current;
    console.log(`[Trigger Check] SHOULD_FETCH = ${shouldFetch}`);
    
    if (shouldFetch) {
      console.log(`[SCHEDULING FETCH] Setting up initial fetch with 1.5s delay for dashboard: ${dashboardName}`);
      // Use a timeout to avoid multiple rapid calls
      fetchTimeoutRef.current = setTimeout(() => {
        fetchSummary();
        fetchTimeoutRef.current = null;
      }, 1500);
    } else {
      console.log(`[SKIPPING FETCH] Conditions not met - check logs above for details`);
    }
    
    // Cleanup function
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [dashboardId, dashboardName, fetchSummary, lastFetchedDashboardId, id, widgetContents]);

  // Handle content changes (separate from dashboard changes)
  useEffect(() => {
    // Skip if loading, no dashboard, or initial fetch not done
    if (loading || !dashboardId || !dashboardName || !initialFetchDoneRef.current) {
      console.log(`Content change skip: loading=${loading}, hasDashboard=${!!dashboardId}, initialDone=${initialFetchDoneRef.current}`);
      return;
    }
    
    // Generate hash of current content
    const currentContentHash = generateContentHash();
    
    // Skip if content hasn't changed significantly
    if (currentContentHash === lastContentHash) {
      console.log("Content change skip: hash unchanged");
      return;
    }
    
    // Skip if no content or API call in progress
    const hasContent = Object.keys(widgetContents || {}).filter(key => key !== id).length > 0;
    if (!hasContent || apiCallInProgressRef.current) {
      console.log(`Content change skip: hasContent=${hasContent}, apiInProgress=${apiCallInProgressRef.current}`);
      return;
    }
    
    console.log("Content changed significantly, setting up fetch with 3s delay");
    
    // Clear any existing timeout
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = null;
    }
    
    // Set timeout for fetch with a longer delay to allow for batched content changes
    fetchTimeoutRef.current = setTimeout(() => {
      fetchSummary();
      fetchTimeoutRef.current = null;
    }, 3000);
    
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
    };
  }, [widgetContents, dashboardId, dashboardName, generateContentHash, lastContentHash, loading, fetchSummary, id]);

  // Handle prompt changes that require refresh
  useEffect(() => {
    // Skip if no dashboard or initial fetch not done
    if (!dashboardId || !dashboardName || !initialFetchDoneRef.current) return;
    
    // If prompt changed and we've already fetched once, trigger a refresh
    if (prompt !== tempPrompt && initialFetchDoneRef.current) {
      console.log("Prompt changed after initial fetch, will refresh after save");
    }
  }, [prompt, tempPrompt, dashboardId, dashboardName]);

  // Handle edit button click
  const handleEdit = () => {
    setTempPrompt(prompt);
    setEditing(true);
  };

  // Handle save button click
  const handleSave = async () => {
    if (loading || apiCallInProgressRef.current) {
      console.log("Save skipped - already loading");
      return;
    }
    
    setPrompt(tempPrompt);
    setEditing(false);

    // Save to database if needed
    try {
      let escapedPrompt = tempPrompt.replace(/'/g, "''");
      const updateQuery = `
        UPDATE widgets
        SET tool_prompt = '${escapedPrompt || ''}'
        WHERE id = '${id}';
      `;
      console.log("Saving prompt to database:", updateQuery);
      const body = { query: updateQuery };

      const response = await fetch(RUN_UPDATE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error("Failed to update widget in the database");
      }

      console.log("Prompt saved successfully, triggering refresh");
      
      // Reset tracking variables to force a new fetch
      setLastFetchedDashboardId(null);
      setLastContentHash("");
      initialFetchDoneRef.current = false;
      
      // Cancel any in-progress API call
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      
      // Clear any existing timeout
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }

      // Show loading state
      setLoading(true);
      setSummary({
        summary_points: [
          { title: "Refreshing Summary", content: "Regenerating dashboard overview with new prompt..." },
          { title: "Please Wait", content: "Analyzing widget data..." },
          { title: "Processing", content: "Gathering insights from your dashboard..." },
          { title: "Almost Ready", content: "Preparing updated summary..." }
        ],
        overall_summary: "Refreshing dashboard summary with updated prompt..."
      });
      
      // Add a small delay before fetching to ensure state updates
      setTimeout(() => {
        fetchSummary(true);
      }, 500);

    } catch (err) {
      console.error("❌ Error updating widget in DB:", err);
      setError(`Failed to save prompt: ${err.message}`);
    }
  };

  // Handle cancel button click
  const handleCancel = () => {
    setTempPrompt(prompt);
    setEditing(false);
  };

  // Handle manual refresh
  const handleRefresh = () => {
    // Skip if already loading
    if (loading || apiCallInProgressRef.current) {
      console.log("Refresh skipped - already loading");
      return;
    }
    
    console.log("Manual refresh triggered");
    
    // Cancel any in-progress API call
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    // Clear any existing timeout
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = null;
    }
    
    // Reset tracking variables
    setLastFetchedDashboardId(null);
    setLastContentHash("");
    initialFetchDoneRef.current = false;
    
    // Show loading state
    setLoading(true);
    setSummary({
      summary_points: [
        { title: "Refreshing Summary", content: "Regenerating dashboard overview..." },
        { title: "Please Wait", content: "Analyzing widget data..." },
        { title: "Processing", content: "Gathering insights from your dashboard..." },
        { title: "Almost Ready", content: "Preparing updated summary..." }
      ],
      overall_summary: "Refreshing dashboard summary..."
    });
    
    // Fetch summary with a small delay
    setTimeout(() => {
      fetchSummary(true);
    }, 100);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cancel any pending operations when component unmounts
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Render the widget
  return (
    <div 
      className="dashboard-widget relative p-4 rounded-lg bg-white shadow-md border border-gray-100 mb-6 mt-4 ml-4 mr-4"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="widget-drag-handle text-lg font-semibold text-primary cursor-move">
          {title}
        </div>
        {!editing && isHovering && (
          <div className="flex items-center">
            <button
              className="widget-edit-btn no-drag bg-transparent border-0 cursor-pointer p-0 ml-2"
              onClick={handleEdit}
              title="Edit"
              type="button"
            >
              <Pencil size={15} className="text-primary" />
            </button>
          </div>
        )}
        {editing && (
          <div className="flex items-center gap-2">
            <button
              className="no-drag bg-primary text-white border-0 rounded py-2 px-4 cursor-pointer"
              onClick={handleSave}
              type="button"
            >
              Save
            </button>
            <button
              className="no-drag bg-gray-200 text-primary border-0 rounded py-2 px-4 cursor-pointer"
              onClick={handleCancel}
              type="button"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Edit mode */}
      {editing ? (
        <div className="flex flex-col gap-2 mt-2 mb-4">
          <textarea
            className="no-drag p-3 border border-gray-300 bg-gray-50 outline-none h-30 rounded-md text-sm resize-y"
            value={tempPrompt}
            onChange={e => setTempPrompt(e.target.value)}
            placeholder="Custom prompt for dashboard summary"
          />
        </div>
      ) : (
        <>
          {/* Loading state */}
          {loading && (
            <div className="w-full min-h-[180px] h-[180px] bg-gradient-to-r from-blue-50 via-gray-50 to-blue-50 bg-[length:200%_100%] animate-shimmer my-4 flex flex-col items-center justify-center rounded-md relative">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mb-3"></div>
              <span className="text-primary font-semibold">Refreshing dashboard summary...</span>
              <style jsx>{`
                @keyframes shimmer {
                  0% { background-position: -200% 0; }
                  100% { background-position: 200% 0; }
                }
                .animate-shimmer {
                  animation: shimmer 1.2s linear infinite;
                }
              `}</style>
            </div>
          )}

          {/* Error state */}
          {error && <div className="text-red-600 my-4">{error}</div>}

          {/* Summary content */}
          {!loading && !error && (
            <div className="bg-white max-w-full relative">
              {/* Overall Summary */}
              <div className="font-semibold text-gray-700 mb-4 p-3 bg-gray-50 rounded-lg border-l-4 border-primary leading-relaxed">
                {parseFormattedText(summary.overall_summary)}
              </div>
              
              {/* Summary Points */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full">
                {summary.summary_points.map((point, index) => (
                  <div 
                    key={index} 
                    className="bg-gray-50 rounded-lg p-3 flex flex-col shadow-sm border border-gray-200 transition-all duration-200 hover:translate-y-[-2px] hover:shadow-md summary-point"
                    style={{animationDelay: `${index * 100}ms`}}
                  >
                    <div className="font-semibold text-primary mb-2 text-[15px]">
                      {parseFormattedText(point.title)}
                    </div>
                    <div className="text-xs text-gray-700 leading-relaxed">
                      {parseFormattedText(point.content)}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Refresh button */}
              <div className="mt-4 text-right">
                <button
                  onClick={handleRefresh}
                  className="no-drag bg-transparent border-0 text-primary text-xs cursor-pointer py-1 px-2 rounded-md inline-flex items-center gap-1 hover:bg-blue-50 disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={loading || apiCallInProgressRef.current}
                >
                  <RefreshCw size={14} />
                  {loading ? 'Refreshing...' : 'Refresh Summary'}
                </button>
              </div>
              
              <style jsx>{`
                @keyframes fadeIn {
                  from { opacity: 0; transform: translateY(10px); }
                  to { opacity: 1; transform: translateY(0); }
                }
                
                .summary-point {
                  opacity: 0;
                  animation: fadeIn 0.5s ease forwards;
                }
              `}</style>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default DashboardSummaryWidget;